/**
 * SettingsPanel Component
 * Inline settings panel for generator nodes (DEPRECATED - use NodeSettingsFooter instead)
 * Displays model-specific parameters (dropdowns, toggles)
 * with the node's type color as background
 * Renders inside the node and expands it when open
 */

import React, { memo, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Slider } from '@mui/material';
import './SettingsPanel.css';

export interface SettingsFieldConfig {
  id: string;
  label: string;
  type: 'select' | 'toggle' | 'slider' | 'model' | 'boolean';
  options?: Array<{ value: string; label: string }>;
  models?: Array<{ id: string; name: string; description?: string }>;
  min?: number;
  max?: number;
  step?: number;
  infoTooltip?: string;
  description?: string;
  default?: string | boolean | number;
}

export interface SettingsPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Node type color for panel background */
  color: string;
  /** Field configurations */
  fields: SettingsFieldConfig[];
  /** Current field values */
  values: Record<string, any>;
  /** Callback when a field value changes */
  onChange: (fieldId: string, value: any) => void;
}

/**
 * SettingsField Component
 * Renders a single setting field (dropdown or toggle)
 */
interface SettingsFieldProps {
  config: SettingsFieldConfig;
  value: any;
  onChange: (value: any) => void;
  textColor: string;
}

const SettingsField: React.FC<SettingsFieldProps> = memo(({ 
  config, 
  value, 
  onChange,
  textColor 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSelect = useCallback((optionValue: string) => {
    onChange(optionValue);
    setIsDropdownOpen(false);
  }, [onChange]);

  const handleToggle = useCallback(() => {
    onChange(!value);
  }, [onChange, value]);

  // Find current option label for select fields
  const currentLabel = config.type === 'select'
    ? config.options?.find(opt => opt.value === value)?.label || value || 'Select...'
    : null;

  return (
    <div className="settings-field nodrag">
      {/* Label Row */}
      <div className="settings-field-label" style={{ color: textColor }}>
        <span>{config.label}</span>
        {config.infoTooltip && (
          <InfoOutlinedIcon 
            className="settings-field-info" 
            titleAccess={config.infoTooltip}
            style={{ color: textColor }}
          />
        )}
      </div>

      {/* Value Row */}
      {config.type === 'select' ? (
        <div className="settings-field-dropdown" ref={dropdownRef}>
          <button
            className="settings-field-dropdown-trigger nodrag"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{ color: textColor }}
          >
            <span className="settings-field-value">{currentLabel}</span>
            <KeyboardArrowDownIcon 
              className={`settings-field-chevron ${isDropdownOpen ? 'open' : ''}`}
              style={{ color: textColor }}
            />
          </button>
          
          {isDropdownOpen && (
            <div className="settings-field-options">
              {config.options?.map(option => (
                <button
                  key={option.value}
                  className={`settings-field-option ${option.value === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : config.type === 'toggle' ? (
        <button
          className={`settings-toggle ${value ? 'on' : 'off'}`}
          onClick={handleToggle}
          aria-pressed={value}
        >
          <span className="settings-toggle-knob" />
        </button>
      ) : config.type === 'slider' ? (
        <div className="settings-slider-container nodrag">
          <Slider
            value={typeof value === 'number' ? value : (config.default as number)}
            onChange={(_, newValue) => onChange(newValue as number)}
            min={config.min ?? 0}
            max={config.max ?? 100}
            step={config.step ?? 1}
            valueLabelDisplay="auto"
            className="settings-slider nodrag"
            sx={{
              color: textColor,
              height: 4,
              padding: '8px 0',
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: textColor,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: `0 0 0 4px rgba(43, 43, 43, 0.2)`,
                },
              },
              '& .MuiSlider-track': {
                backgroundColor: textColor,
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(43, 43, 43, 0.3)',
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: textColor,
                color: '#fff',
                fontSize: 11,
              },
            }}
          />
          <span className="settings-slider-value" style={{ color: textColor }}>{value}</span>
        </div>
      ) : null}
    </div>
  );
});

SettingsField.displayName = 'SettingsField';

/**
 * SettingsPanel Component
 * Renders inline within the node
 */
const SettingsPanelComponent: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  color,
  fields,
  values,
  onChange
}) => {
  // Determine text color based on background luminance
  // For our known colors: #42C3D7 (image), #FF4B42 (video), #FED402 (text)
  // All need dark text (#2B2B2B) for readability
  const textColor = '#2B2B2B';

  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    onChange(fieldId, value);
  }, [onChange]);

  if (!isOpen) return null;

  return (
    <div 
      className="settings-panel nodrag"
      style={{ backgroundColor: color }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Close Button */}
      <button 
        className="settings-panel-close"
        onClick={onClose}
        aria-label="Close settings"
      >
        <CloseIcon style={{ fontSize: 16 }} />
      </button>

      {/* Fields */}
      <div className="settings-panel-fields">
        {fields.map(field => (
          <SettingsField
            key={field.id}
            config={field}
            value={values[field.id] ?? field.default}
            onChange={(value) => handleFieldChange(field.id, value)}
            textColor={textColor}
          />
        ))}
      </div>
    </div>
  );
};

export const SettingsPanel = memo(SettingsPanelComponent);
export default SettingsPanel;
